import { useTheme } from "@mui/material";
import { MessageCircle, Sparkles, Target } from "lucide-react";
import { type FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ownHomeImg from "shared/assets/inspiration/ins-001-own-home.jpg";
import { mapGoalFormToParams } from "web_myplann/Goals/Mappers/mapGoalFormToParams";
import { GoalCategoryTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { AffordabilityEditSheet } from "web_myplann/Inspiration/Components/AffordabilityEditSheet";
import { ExpensesSheet } from "web_myplann/Inspiration/Components/ExpensesSheet";
import { InspirationDetailLayout } from "web_myplann/Inspiration/Components/InspirationDetailLayout";
import { OwnHomeColdIntro } from "web_myplann/Inspiration/Components/OwnHomeColdIntro";
import { OwnHomeCreateGoalSheet } from "web_myplann/Inspiration/Components/OwnHomeCreateGoalSheet";
import { OwnHomeEstimateSheet } from "web_myplann/Inspiration/Components/OwnHomeEstimateSheet";
import { OwnHomeResult } from "web_myplann/Inspiration/Components/OwnHomeResult";
import { useOwnHomeJourney } from "web_myplann/Inspiration/Hooks/useOwnHomeJourney";
import { computeGoalPreset } from "web_myplann/Inspiration/Utils/ownHomeMath";
import { useContactAdvisor } from "web_myplann/Shared/Hooks/useContactAdvisor";

const PageResources = Resources.Inspiration.OwnHome;

export const OwnHomeInspirationPage: FC = () => {
	const { t } = useResource();
	const theme = useTheme();
	const navigate = useNavigate();
	const { openChat } = useContactAdvisor();
	const {
		context,
		activeSheet,
		openSheet,
		closeSheet,
		submitQuick,
		submitExpenses,
		submitAffordabilityEdit,
	} = useOwnHomeJourney();

	const isCold = context.state === "cold";

	const coldCta = {
		label: t(PageResources.cta),
		icon: <Sparkles size={theme.iconSize.md} />,
		onClick: () => openSheet("quick"),
	};

	const goToFinances = () => navigate(Routing.Finances.Intro.path());

	const goalPreset = useMemo(() => computeGoalPreset(context), [context]);

	const navigateToGoalForm = () => {
		const params = mapGoalFormToParams({
			category: GoalCategoryTypeEnum.House,
			totalValue: goalPreset.totalValue,
			ownFunds: goalPreset.ownFunds,
			loanAmount: goalPreset.loanAmount,
		});
		navigate(`${Routing.Goals.GoalFormCreate.path()}?${params.toString()}`);
	};

	return (
		<>
			<InspirationDetailLayout
				scrollResetKey={isCold}
				image={ownHomeImg}
				category={t(PageResources.category)}
				title={t(PageResources.title)}
				subtitle={t(PageResources.subtitle)}
				primaryCta={
					isCold
						? coldCta
						: {
								label: t(PageResources.Result.createGoal),
								icon: <Target size={theme.iconSize.md} />,
								onClick: () => openSheet("create-goal"),
							}
				}
				secondaryCta={
					isCold
						? undefined
						: {
								label: t(PageResources.Result.contactAdvisor),
								icon: <MessageCircle size={theme.iconSize.md} />,
								variant: "tertiary",
								onClick: () => openChat(t(PageResources.Result.advisorMessage)),
							}
				}
			>
				{isCold ? (
					<OwnHomeColdIntro />
				) : (
					<OwnHomeResult
						context={context}
						onEditAffordability={() => openSheet("affordability-edit")}
						onAddExpenses={() => openSheet("expenses")}
					/>
				)}
			</InspirationDetailLayout>

			<OwnHomeEstimateSheet
				open={activeSheet === "quick"}
				onClose={closeSheet}
				onSubmit={submitQuick}
				onSaveToBilance={goToFinances}
			/>

			<ExpensesSheet
				open={activeSheet === "expenses"}
				initial={context.expenses}
				onClose={closeSheet}
				onSubmit={submitExpenses}
				onSaveToBilance={goToFinances}
			/>

			<AffordabilityEditSheet
				open={activeSheet === "affordability-edit"}
				initialIncome={context.income}
				initialExpenses={context.expenses}
				showExpenses={context.state === "full"}
				onClose={closeSheet}
				onSubmit={submitAffordabilityEdit}
			/>

			<OwnHomeCreateGoalSheet
				open={activeSheet === "create-goal"}
				preset={goalPreset}
				onClose={closeSheet}
				onContinue={navigateToGoalForm}
			/>
		</>
	);
};
